import { FileText, X, Upload, Eye, Download, Target, Paperclip, CircleCheck } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from 'lib/utils'

import { AdasBox, AdasLinearProgress, AdasTypography } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useSubmitFileAttachment, removeAttachment, getFile, getClientInvoiceFile } from '../../../api/api'
import { formatTime } from '../../../utils/utils'
import { MIME_TYPES_MAP, ATTACHMENT_TYPE_MAP } from '../../../variable-maps'
import { useMessageStore } from '../../../store'
import { Logger } from '../../../logger'
import { AdasLoader } from '@components/adas-loader'

const BYTES_IN_MB = 1000000

export const FileUpload = (props) => {
  const {
    typeLabel,
    title,
    workOrderData,
    existingData,
    removeFile,
    additionalDocument,
    isClientInvoice = false,
    disabled: disabledProp = false,
    type = 'quotes',
    variant = 'card',
    description
  } = props
  // Uploading and removing are writes; downloading stays available.
  const isReadOnly = useIsReadOnly()
  const disabled = disabledProp || isReadOnly
  const [data, setData] = useState(null)
  const [fileURL, setFileURL] = useState('')
  const displayData = data ? data : existingData
  const fileRef = useRef(null)
  const { timeLeft, progress, uploadData, isSuccess, attachmentId, resetIsSuccess } = useSubmitFileAttachment()
  const addMessage = useMessageStore((store) => store.addMessage)
  const [loading, setLoading] = useState(false)

  const handleUpload = () => {
    const file = fileRef.current.files[0]
    if (!file) {
      return
    }
    const suppoprtedFileTypes = Object.values(MIME_TYPES_MAP)
    if (!suppoprtedFileTypes.includes(file.type)) {
      addMessage({ text: 'File format not supported!' })
      return
    }
    setData(file)
  }

  const truncateFileName = useCallback((fileName) => {
    if (fileName.length <= 80) return fileName
    const extStart = fileName.lastIndexOf('.')
    let ext = fileName.slice(extStart, fileName.length)
    let name = fileName.slice(0, 80 - ext.length)
    return name + ext
  }, [])

  const uploadFile = async (file, docType) => {
    const formData = new FormData()
    formData.append('file', file, truncateFileName(file.name))
    let attachmentType = ATTACHMENT_TYPE_MAP[docType]
    const params = {
      data: formData,
      workorderId: workOrderData.id,
      attachmentType,
      type,
      onFailed: () => setData(null)
    }
    const response = uploadData(params)
    response.then(() => {}).catch(() => setData(null))
  }

  const handleClose = async () => {
    if (!disabled) {
      const params = {
        workorderId: workOrderData.id,
        attachmentId: existingData ? existingData.id : attachmentId
      }
      const removeResponse = await removeAttachment(params)
      if (removeResponse.status === 200) {
        if (existingData || additionalDocument) {
          removeFile()
        }
        setData(null)
      }
    }
  }

  const createUrl = (data) => {
    const extIndex = data.name.lastIndexOf('.')
    const fileExt = data.name.slice(extIndex, data.name.length)
    const fileBlob = new Blob([data], {
      type: MIME_TYPES_MAP[fileExt.toLowerCase()] || 'application/octet-stream'
    })
    return URL.createObjectURL(fileBlob)
  }

  useEffect(() => {
    const fetchData = async () => {
      // Caller already preloaded the blob URL (e.g., QuoteDocument batches all
      // file fetches under a single page-level loader) — skip the per-tile fetch
      // and use the supplied URL directly.
      if (existingData?.preloadedUrl) {
        setFileURL(existingData.preloadedUrl)
        if (existingData.preloadedSize != null) displayData.size = existingData.preloadedSize
        return
      }

      if (existingData) {
        try {
          setLoading(true)
          let response = null
          if (isClientInvoice) {
            response = await getClientInvoiceFile({
              token: workOrderData.tempToken,
              invoiceId: workOrderData.id,
              attachmentId: existingData.id
            })
          } else {
            response = await getFile({
              attachmentId: existingData.id
            })
          }

          const fileValue = new File([response.data], existingData.name)
          fileValue.id = existingData.id
          displayData.size = fileValue.size
          const url = createUrl(fileValue)
          setFileURL(url)
        } catch (error) {
          Logger.error({ message: error, payload: { existingData } })
        } finally {
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      URL.revokeObjectURL(fileURL)
    }
    /* eslint-disable */
  }, [existingData])

  useEffect(() => {
    if (data) {
      uploadFile(data, typeLabel)
      setFileURL(createUrl(data))

      return () => {
        URL.revokeObjectURL(fileURL)
      }
    } else {
      resetIsSuccess()
    }
    /* eslint-disable */
  }, [data])

  useEffect(() => {
    if (additionalDocument && !additionalDocument.removed) {
      setData(additionalDocument)
    } else {
      resetIsSuccess()
    }
    /* eslint-disable */
  }, [additionalDocument])

  const downloadFile = () => {
    if (!fileURL) return
    const a = document.createElement('a')
    a.href = fileURL
    a.download = existingData?.name || data?.name || 'document'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const sourceLabel = existingData?.source || existingData?.attachment_source
  const referenceLabel = existingData?.reference_label || existingData?.reference_number
  const aiLinked = Boolean(existingData?.ai_auto_linked || existingData?.aiAutoLinked)

  const fileSizeBytes = displayData?.size ?? existingData?.fileSize ?? existingData?.file_size ?? 0
  const fileSizeMb = fileSizeBytes ? `${(fileSizeBytes / BYTES_IN_MB).toFixed(2)} MB` : ''
  const fileName = existingData?.name || data?.name || ''
  // Saved on the server: a loaded attachment, or a new upload that succeeded (not one still uploading).
  const isAttached = Boolean(existingData) || isSuccess

  if (variant === 'row') {
    if (!displayData) {
      return (
        !isClientInvoice && (
          <label
            className={cn(
              'flex items-center justify-between gap-4 rounded-xl border border-[#D1D5D8] bg-white px-4 py-3 transition-colors',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#F8FAFC]'
            )}
          >
            <AdasBox className='flex items-center gap-3 min-w-0'>
              <AdasBox className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E8EFFF]'>
                <Upload className='h-5 w-5 text-[#0A1628]' strokeWidth={1.75} />
              </AdasBox>
              <AdasBox className='min-w-0'>
                <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>{props.label || title}</AdasTypography>
                {description && <AdasTypography className='mt-0.5 text-[13px] text-[#6B7280]'>{description}</AdasTypography>}
              </AdasBox>
            </AdasBox>
            <span
              className={cn(
                'shrink-0 rounded-full bg-[#1F6FEB] px-5 py-2 text-[12px] font-bold uppercase tracking-wide text-white transition-colors',
                !disabled && 'hover:bg-[#1A5FD0]'
              )}
            >
              Upload
            </span>
            <input
              ref={fileRef}
              type='file'
              hidden
              disabled={disabled}
              onChange={handleUpload}
              accept='.jpg, .jpeg, .png, .pdf, .doc, .docx, .txt'
            />
          </label>
        )
      )
    }
    if (loading) {
      return <AdasLoader className='rounded-xl min-h-[64px]' />
    }
    // CAR-4069: an attached file reads as attached (green edge, check, "Attached" pill) and has a
    // visible View; theme tokens only.
    return (
      <AdasBox
        className={cn(
          'flex items-center justify-between gap-4 rounded-xl border px-4 py-3',
          isAttached ? 'border-success bg-card' : 'border-border bg-card'
        )}
      >
        <AdasBox className='flex items-center gap-3 min-w-0'>
          <AdasBox
            className={cn(
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg',
              isAttached ? 'bg-success-light' : 'bg-[#E8EFFF]'
            )}
          >
            {isAttached ? (
              <CircleCheck className='h-5 w-5 text-success' strokeWidth={1.75} />
            ) : (
              <Paperclip className='h-5 w-5 text-primary' strokeWidth={1.75} />
            )}
          </AdasBox>
          <AdasBox className='min-w-0'>
            <AdasBox className='flex flex-wrap items-center gap-x-2 gap-y-1'>
              <AdasTypography className='text-[15px] font-bold text-foreground'>{props.label || title}</AdasTypography>
              {isAttached && (
                <span className='inline-flex items-center gap-1 rounded-full bg-success-light px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-foreground'>
                  <span aria-hidden='true' className='h-1.5 w-1.5 rounded-full bg-success' />
                  Attached
                </span>
              )}
            </AdasBox>
            <a
              href={fileURL}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => {
                if (!fileURL) e.preventDefault()
              }}
              title={fileName}
              className='mt-0.5 block truncate text-[13px] font-medium text-primary underline-offset-2 hover:underline'
            >
              {fileName}
              {fileSizeMb && <span className='ml-2 font-mono text-[12px]'>{fileSizeMb}</span>}
            </a>
          </AdasBox>
        </AdasBox>
        <AdasBox className='flex shrink-0 items-center gap-2'>
          {fileURL && (
            <a
              href={fileURL}
              target='_blank'
              rel='noopener noreferrer'
              aria-label='View file'
              title='View'
              className='inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-muted'
            >
              <Eye className='h-4 w-4' />
            </a>
          )}
          <button
            type='button'
            onClick={downloadFile}
            disabled={!fileURL}
            aria-label='Download file'
            className='inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-muted disabled:opacity-40'
          >
            <Download className='h-4 w-4' />
          </button>
          {!isClientInvoice && (
            <button
              type='button'
              onClick={handleClose}
              disabled={disabled}
              aria-label='Remove file'
              className='inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#475569] hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </AdasBox>
        <span className='hidden'>
          <FileText />
          <Eye />
          <Target />
        </span>
      </AdasBox>
    )
  }

  return !displayData ? (
    !isClientInvoice && (
      <label
        className={cn(
          'flex flex-col items-center rounded-xl border-2 border-dashed border-[#D1D5D8] bg-white px-4 pt-12 pb-8 min-h-[260px] transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#F8FAFC]'
        )}
      >
        <Upload className='h-8 w-8 text-[#475569]' strokeWidth={1.75} />
        <AdasTypography className='mt-3 w-full text-center text-[12px] font-bold uppercase tracking-wide text-[#0A1628] leading-snug break-words'>
          {title}
        </AdasTypography>
        <AdasTypography className='mt-2 w-full text-center text-[11px] text-[#6B7280] leading-snug break-words'>
          (.pdf, .docx, .doc, .png, .jpeg, .txt)
        </AdasTypography>
        <input
          ref={fileRef}
          type='file'
          hidden
          disabled={disabled}
          onChange={handleUpload}
          accept='.jpg, .jpeg, .png, .pdf, .doc, .docx, .txt'
        />
      </label>
    )
  ) : loading ? (
    <AdasLoader className='rounded-xl min-h-[200px]' />
  ) : (
    <AdasBox className='relative flex flex-col rounded-xl border border-[#D1D5D8] bg-white'>
      {!isClientInvoice && (
        <button
          type='button'
          onClick={handleClose}
          disabled={disabled}
          aria-label='Remove file'
          className='absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white text-[#475569] hover:bg-[#F1F4F8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      )}
      {/* Paperclip + filename anchored to top so the icon aligns horizontally
          with the upload icon in empty tiles (both use pt-12). */}
      <AdasBox className='flex flex-col items-center text-center px-4 pt-12 pb-8'>
        <Paperclip className='h-8 w-8 text-[#1F6FEB]' strokeWidth={1.75} />
        <a
          href={fileURL}
          target='_blank'
          rel='noopener noreferrer'
          onClick={(e) => {
            if (!fileURL) e.preventDefault()
          }}
          title={fileName || props.label}
          className='mt-3 block w-full text-[14px] font-medium text-[#0A1628] truncate hover:underline'
        >
          {fileName || props.label}
        </a>
      </AdasBox>
      <hr className='border-[#EBEDF0]' />
      {/* Bottom section — slot label + size, left-aligned */}
      <AdasBox className='px-4 py-4'>
        <AdasTypography className='text-[14px] font-bold text-[#0A1628]'>{props.label || ''}</AdasTypography>
        {fileSizeMb && <AdasTypography className='mt-0.5 font-mono text-[13px] text-[#475569]'>{fileSizeMb}</AdasTypography>}
        {(sourceLabel || referenceLabel) && (
          <AdasBox className='mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-[#6B7280]'>
            {sourceLabel && <span>{sourceLabel}</span>}
            {referenceLabel && <span>{referenceLabel}</span>}
          </AdasBox>
        )}
        {aiLinked && (
          <AdasBox className='mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1F6FEB]'>
            <Target className='h-3 w-3' />
            AI Auto-linked
          </AdasBox>
        )}
        {!existingData && progress && !isSuccess && (
          <AdasBox className='mt-2 w-full'>
            <AdasLinearProgress value={progress} />
            <AdasBox className='mt-1 flex items-center justify-between'>
              <AdasTypography className='text-xs text-muted-foreground'>{(displayData.size / BYTES_IN_MB).toFixed(2)} MB</AdasTypography>
              <AdasBox className='flex'>
                <AdasTypography className='text-xs text-muted-foreground'>{formatTime(timeLeft)}</AdasTypography>
                <AdasTypography className='ml-[10px] text-xs text-muted-foreground'>{progress}%</AdasTypography>
              </AdasBox>
            </AdasBox>
          </AdasBox>
        )}
      </AdasBox>
      {/* Kept in scope to avoid an unused-import lint when these icons aren't used inline anymore */}
      <span className='hidden'>
        <FileText />
        <Eye />
        <Download />
      </span>
    </AdasBox>
  )
}

FileUpload.propTypes = {
  typeLabel: PropTypes.string,
  type: PropTypes.string,
  title: PropTypes.string,
  workOrderData: PropTypes.object,
  existingData: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  removeFile: PropTypes.func,
  additionalDocument: PropTypes.object,
  isClientInvoice: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['card', 'row']),
  description: PropTypes.string,
  label: PropTypes.string
}
